import { Handle, type Position } from "@xyflow/react"
import { cn } from "@/lib/utils"
import { NodePill } from "./primitives/node-pill"
import type { NodeHandleTone } from "./node-appearance"
import { NodeStateDot } from "./node-state-dot"

const SHOW_HANDLE_HITBOX_DEBUG = false

function BaseHandle({
	type,
	position,
	id,
	className,
	hitboxSize,
	tone,
	anchorKind = "socket",
	decorated,
	style,
}: {
	type: "source" | "target"
	position: Position
	id?: string
	className?: string
	hitboxSize: number
	tone: NodeHandleTone
	anchorKind?: "socket" | "pill"
	decorated?: boolean
	style?: React.CSSProperties
}) {
	const debugBorderColor =
		type === "target" ? "rgb(239 68 68)" : "rgb(59 130 246)"
	const debugBackgroundColor =
		type === "target" ? "rgba(239, 68, 68, 0.35)" : "rgba(59, 130, 246, 0.35)"
	const hitAreaStyle = {
		width: hitboxSize,
		height: hitboxSize,
		minWidth: hitboxSize,
		minHeight: hitboxSize,
		...(style ?? {}),
	}
	const usesOverlayWrapper = decorated === false
	const wrapperClassName = usesOverlayWrapper
		? "absolute inset-0 z-10"
		: "relative inline-flex h-full w-full shrink-0 items-center justify-center"

	return (
		<span className={wrapperClassName}>
			{decorated !== false ? (
				<NodePill
					tone="socket"
					className="pointer-events-none items-center justify-center"
				>
					<NodeStateDot tone={tone} />
				</NodePill>
			) : null}
			<Handle
				type={type}
				position={position}
				id={id}
				data-oblique-handle-kind={anchorKind}
				data-oblique-handle-tone={tone}
				className={cn(
					"!absolute !inset-0 !m-0 !flex !items-center !justify-center !overflow-visible !border-0 !bg-transparent !p-0 !opacity-0 group/handle",
					className,
				)}
				style={{
					left: 0,
					top: 0,
					right: "auto",
					bottom: "auto",
					width: hitboxSize,
					height: hitboxSize,
					minWidth: hitboxSize,
					minHeight: hitboxSize,
					transform: "none",
					...(style ?? {}),
				}}
			>
				<span
					aria-hidden
					className={cn(
						"absolute inset-0",
						SHOW_HANDLE_HITBOX_DEBUG
							? "pointer-events-auto rounded-full"
							: "pointer-events-auto bg-transparent",
					)}
					style={{
						...hitAreaStyle,
						...(SHOW_HANDLE_HITBOX_DEBUG
							? {
									backgroundColor: debugBackgroundColor,
									boxShadow: `inset 0 0 0 2px ${debugBorderColor}`,
								}
							: undefined),
					}}
				/>
			</Handle>
		</span>
	)
}

export function InputHandle({
	position,
	id,
	className,
	tone = "muted",
	anchorKind = "socket",
	decorated = true,
	style,
}: {
	position: Position
	id?: string
	className?: string
	tone?: NodeHandleTone
	anchorKind?: "socket" | "pill"
	decorated?: boolean
	style?: React.CSSProperties
}) {
	return (
		<BaseHandle
			type="target"
			position={position}
			id={id}
			className={className}
			hitboxSize={22}
			tone={tone}
			anchorKind={anchorKind}
			decorated={decorated}
			style={style}
		/>
	)
}

export function OutputHandle({
	position,
	id,
	className,
	tone = "neutral",
	anchorKind = "socket",
	decorated = true,
	style,
}: {
	position: Position
	id?: string
	className?: string
	tone?: NodeHandleTone
	anchorKind?: "socket" | "pill"
	decorated?: boolean
	style?: React.CSSProperties
}) {
	return (
		<BaseHandle
			type="source"
			position={position}
			id={id}
			className={className}
			hitboxSize={22}
			tone={tone}
			anchorKind={anchorKind}
			decorated={decorated}
			style={style}
		/>
	)
}
